import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPasswordReset} from '../core/_requests'
import {validateRut, formatRut} from '../../../Functions'

const initialValues = {
  username: '',
}

const forgotPasswordSchema = Yup.object().shape({
  username: Yup.string()
    .required('El RUT es obligatorio')
    .test('rut-validation', 'RUT inválido', (value) => validateRut(value || '')),
})

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      requestPasswordReset(values.username)
        .then(() => {
          setHasErrors(false)
          setLoading(false)
        })
        .catch(() => {
          setHasErrors(true)
          setLoading(false)
          setSubmitting(false)
        })
    },
  })

  const onChangeRut = async (ev: any) => {
    const formattedRut = formatRut(ev.target.value)
    await formik.setFieldValue('username', formattedRut)
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework p-4'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-primary fw-bolder mb-3'>¿Olvidó su contraseña?</h1>
        {/* end::Title */}

        <div className='text-gray-500 fw-semibold fs-6'>
          Ingrese su RUT y le enviaremos un correo con instrucciones para restablecer su contraseña.
        </div>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            Ocurrió un error al procesar su solicitud, por favor intente nuevamente.
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>
            Si su RUT se encuentra registrado, recibirá un correo con instrucciones para restablecer su contraseña.
          </div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-700 fs-6'>RUT</label>
        <input
          type='text'
          placeholder='RUT*'
          autoComplete='off'
          {...formik.getFieldProps('username')}
          onChange={onChangeRut}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.username && formik.errors.username},
            {
              'is-valid': formik.touched.username && !formik.errors.username,
            }
          )}
        />
        {formik.touched.username && formik.errors.username && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.username}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button
          type='submit'
          id='kt_password_reset_submit'
          className='btn btn-primary me-4'
          disabled={formik.isSubmitting || !formik.isValid || hasErrors === false}
        >
          {loading ? (
            <span className='indicator-progress' style={{display: 'block'}}>
              Por favor espere...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          ) : (
            <span className='indicator-label'>Enviar</span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={loading}
          >
            Cancelar
          </button>
        </Link>{' '}
      </div>
      {/* end::Form group */}
    </form>
  )
}
